import networkData from "../sampledata/SampleNetwork";
import type { DynamicInputParameters } from "../types/ApiData";

const API_URL = process.env.REACT_APP_API_ENDPOINT 

export interface ApiData {
    id: string,
    city: string,
    country: string,
    timestamp: number,
    sections: SectionData[],
    checked?: boolean
}

export interface LocationResponse {
    [index: string]: SectionData;
}


export interface SectionData {
    location: LocationData,
    recommendations: Recommendations [],
    checked?: boolean
    [key:string]: any
}

export interface LocationData {
    latitude: any,
    longitude: any,
    altitude: number,
    network_id: string,
    section_id: string
}

export interface Recommendations {
    [key: string]: any
}

export interface networkRequestDetails {
    network_id: string
    coords: {latitude: any, longitude: any}
    section_id: string
}

/* This function fetching the results for buckling calculation for railway network */
export const getValuesForRailway = async (inputParameters: DynamicInputParameters, accessToken: string, networkDetails: networkRequestDetails): Promise<any> => {

    const { checked, ...filteredInputParameters } = inputParameters
    const request = getRequestData(filteredInputParameters, networkDetails)
    let response
    let options = {
        method: 'POST',
        data: request
    }

    response = await getResponse(`${API_URL}v2/railway/process`,  accessToken, options)

    const responseData = response.data

    return {
        status: "successful",
        response: responseData,
        errors: response.error
    }
}

const getRequestData = (inputParameters: DynamicInputParameters, networkDetails: networkRequestDetails) => {
    const today = new Date();
    const timestamp = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const location = {
        latitude: networkDetails.coords.latitude,
        longitude: networkDetails.coords.longitude,
        altitude: 30,
        network_id: networkDetails.network_id,
        section_id: networkDetails.section_id
    }

    const request = {
        id: inputParameters.id,
        city: "dusseldorf",
        country: "germany",
        timestamp: timestamp,
        sections: [
            { ...inputParameters, location }
        ]
    }

    return JSON.stringify(request);

}

/* This function fetching the results for buckling calculation for individual geo location point */
const getRequestSectionData = (inputParameters: DynamicInputParameters, coordinates: number[], networkDetails: networkRequestDetails) => {
    const location = {
        latitude: coordinates[0],
        longitude: coordinates[1],
        altitude: 30,
        network_id: networkDetails.network_id,
        section_id: networkDetails.section_id
    }

    const request = {
        ...inputParameters, location
    }

    return JSON.stringify(request);

}

export const getValuesForSections = async (inputParameters: DynamicInputParameters, accessToken: string, coordinates: number[], networkDetails: networkRequestDetails): Promise<any> => {

    const { checked, ...filteredInputParameters } = inputParameters
    const request = getRequestSectionData(filteredInputParameters, coordinates, networkDetails)
    let response
    //let errors: any = ''
    let options = {
        method: 'POST',
        data: request
    }

    response = await getResponse(`${API_URL}v2/section/process`, accessToken, options)

    const responseData = response.data

    return {
        status: "successful",
        response: responseData,
        errors: response.error
    }
}

export const getResponse = async (url: string, accessToken: string, options?: {
        method?: string
        data?: any
        params?: any
    }
) => {
    let response: any
    let errors: any = ''

    const { method = 'GET', data, params } = options || {}

    try {
        // Build query params
        let finalUrl = url
        if (params) {
            const queryString = new URLSearchParams(params).toString()
            finalUrl += `?${queryString}`
        }

        const res = await fetch(finalUrl, {
            method,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: method !== 'GET' && data ? (data) : undefined,
        })

        const responseData = await res.json().catch(() => ({}))

        if (!res.ok) {
            let messages: string | string[] = ''

            const details = responseData?.detail

            if (Array.isArray(details)) {
                if (details.length > 1) {
                    messages = details
                        .map(
                            (item: { msg: string }) =>
                                `<span style="color: red;padding: 3px">•</span>${item.msg}`
                        )
                        .join('<br />')
                } else {
                    messages = details[0]?.msg || ''
                }
            }

            if (res.status === 429) {
                messages = responseData?.message || 'An error occurred'
            }

            if (!messages) {
                messages = 'An error occurred'
            }

            errors = {
                code: 'FETCH_ERROR',
                message: messages,
                status: res.status,
                data: responseData,
            }
        } else {
            response = { data: responseData }
        }
    } catch (error) {
        errors = {
            code: 'UNEXPECTED_ERROR',
            message: (error as Error).message || 'An unknown error occurred',
        }
    }

    return {
        data: response?.data ?? {},
        error: errors,
    }
}

export const getInputParameters = async (accessToken: string): Promise<any> => {
    let options = {
        method: 'GET'
    }

    let response = await getResponse(`${API_URL}v2/parameters/schema`, accessToken, options)

    const responseData = response.data

    return {
        status: "successful",
        response: responseData,
        errors: response.error
    }
}

export type ReportParameterOption = {
  label: string;
  value: string;
};

export type ReportParameter = {
  code: string;
  label: string;
  options: ReportParameterOption[];
};

export interface ReportItem {
  code: string;
  title: string;
  description: string;
  type: string;
  date_range: boolean;
  parameters: ReportParameter[]
}

export interface GetAllReportsResult {
  status: "successful" | "failed";
  response: ReportItem[];
  errors?: unknown;
}

export interface DownloadItem {
    code: string
    start_date: string
    end_date: string
    file_url: string
}

export interface GetDownloadResult {
  status: "successful" | "failed";
  response: DownloadItem;
  errors?: any;
}

export const getAllReports = async (accessToken: string): Promise<GetAllReportsResult> => {
    let options = {
        method: 'GET'
    }

    let response = await getResponse(`${API_URL}v2/reports`, accessToken, options)

    const responseData = response.data

    return {
        status: "successful",
        response: responseData,
        errors: response.error
    }
}

export const downloadReport = async (request: any, accessToken: string): Promise<GetDownloadResult> => {
    let options = {
        method: 'POST',
        data: request ? JSON.stringify(request) : ''
    }

    let response = await getResponse(`${API_URL}v2/reports/get_report`, accessToken, options)

    const responseData = response.data

    return {
        status: "successful",
        response: responseData,
        errors: response.error
    }
}


export interface GetAllStoredParams {
  status: "successful" | "failed";
  response: StoredParams;
  errors?: unknown;
}

export interface StoredParams {
    global_result : SectionData,
    network_id: string,
    points: SectionData[]
}

export const getAllStoredParams = async (accessToken: string, network_id: string): Promise<GetAllStoredParams> => {
    let options = {
        method: 'POST',
        data: JSON.stringify({network_id: network_id})
    }

    let response = await getResponse(`${API_URL}v2/networks/get_data`, accessToken, options)

    const responseData = response.data

    return {
        status: "successful",
        response: responseData,
        errors: response.error
    }
}


export const deleteSectionCalculations = async (accessToken: string, data: any): Promise<any> => {
    let options = {
        method: 'POST',
        data: JSON.stringify(data)
    }

    let response = await getResponse(`${API_URL}v2/networks/delete_sections`, accessToken, options)

    const responseData = response.data

    return {
        status: "successful",
        response: responseData,
        errors: response.error
    }
}

export const copySectionCalculations = async (accessToken: string, data: any): Promise<any> => {
    let options = {
        method: 'POST',
        data: JSON.stringify(data)
    }

    let response = await getResponse(`${API_URL}v2/networks/copy_section`, accessToken, options)

    const responseData = response.data

    return {
        status: "successful",
        response: responseData,
        errors: response.error
    }
}